/**
 * Litigation Masters Service
 *
 * All write operations (create/update/delete) are super-admin only.
 * Read operations (list, get, graph) are available to any authenticated user —
 * used by the GRC entity layer when creating litigation instances.
 *
 * Super-admin UI: import from here using saClient.
 * GRC read-only callers: import the read-only helpers at the bottom.
 */

import { saClient } from './superAdminService';
import apiService from './apiService';

const BASE = '/api/v1/lit';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LitStepClass {
  id: number;
  name: string;
  description: string | null;
  color_hint: string | null;
  is_active: number;
}

export interface LitProceeding {
  id: number;
  name: string;
  description: string | null;
  act_name: string | null;
  sections: string | null;
  penal_provisions: string | null;
  is_active: number;
  step_count: number;
  created_by: number | null;
  created_on: string | null;
  modified_by: number | null;
  modified_on: string | null;
}

export interface LitStepFlowBrief {
  id: number;
  to_step_id: number;
  to_step_name: string | null;
  to_step_class_name: string | null;
  to_step_class_color: string | null;
  condition_label: string | null;
  seq_order: number;
}

export interface LitProceedingStep {
  id: number;
  proceeding_id: number;
  step_name: string;
  step_class_id: number;
  step_class_name: string | null;
  step_class_color: string | null;
  seq_order: number;
  description: string | null;
  expected_duration_days: number | null;
  expected_authority: string | null;
  is_start: boolean;
  is_terminal: boolean;
  is_active: number;
  outgoing_flows: LitStepFlowBrief[];
}

export interface LitStepFlow {
  id: number;
  from_step_id: number;
  to_step_id: number;
  from_step_name: string | null;
  to_step_name: string | null;
  to_step_class_name: string | null;
  to_step_class_color: string | null;
  condition_label: string | null;
  seq_order: number;
}

export interface LitGraphStep {
  id: number;
  step_name: string;
  step_class_name: string | null;
  step_class_color: string | null;
  description: string | null;
  expected_duration_days: number | null;
  expected_authority: string | null;
  is_start: boolean;
  is_terminal: boolean;
}

export interface LitGraphFlow {
  id: number;
  from_step_id: number;
  to_step_id: number;
  condition_label: string | null;
  seq_order: number;
}

export interface LitProceedingGraph {
  proceeding_id: number;
  proceeding_name: string;
  act_name: string | null;
  steps: LitGraphStep[];
  flows: LitGraphFlow[];
}

// ─── Step classes ────────────────────────────────────────────────────────────

export const litStepClassesService = {
  async getAll(): Promise<LitStepClass[]> {
    const { data } = await saClient.get(`${BASE}/step-classes`);
    return data.data;
  },
};

// ─── Proceedings (super-admin mutations) ─────────────────────────────────────

export const litProceedingsService = {
  async getAll(): Promise<LitProceeding[]> {
    const { data } = await saClient.get(`${BASE}/proceedings`);
    return data.data;
  },

  async getById(id: number): Promise<LitProceeding> {
    const { data } = await saClient.get(`${BASE}/proceedings/${id}`);
    return data.data;
  },

  async create(payload: {
    name: string;
    description?: string;
    act_name?: string;
    sections?: string;
    penal_provisions?: string;
  }): Promise<LitProceeding> {
    const { data } = await saClient.post(`${BASE}/proceedings`, payload);
    return data.data;
  },

  async update(id: number, payload: Partial<{
    name: string;
    description: string;
    act_name: string;
    sections: string;
    penal_provisions: string;
    is_active: number;
  }>): Promise<LitProceeding> {
    const { data } = await saClient.put(`${BASE}/proceedings/${id}`, payload);
    return data.data;
  },

  async delete(id: number): Promise<void> {
    await saClient.delete(`${BASE}/proceedings/${id}`);
  },

  async inactivate(id: number): Promise<LitProceeding> {
    return this.update(id, { is_active: 0 });
  },

  async reactivate(id: number): Promise<LitProceeding> {
    return this.update(id, { is_active: 1 });
  },
};

// ─── Steps (super-admin mutations) ───────────────────────────────────────────

export const litStepsService = {
  async getByProceeding(proceedingId: number): Promise<LitProceedingStep[]> {
    const { data } = await saClient.get(`${BASE}/proceedings/${proceedingId}/steps`);
    return data.data;
  },

  async create(proceedingId: number, payload: {
    step_name: string;
    step_class_id: number;
    seq_order?: number;
    description?: string;
    expected_duration_days?: number | null;
    expected_authority?: string;
    is_start?: boolean;
    is_terminal?: boolean;
  }): Promise<LitProceedingStep> {
    const { data } = await saClient.post(`${BASE}/proceedings/${proceedingId}/steps`, payload);
    return data.data;
  },

  async update(stepId: number, payload: Partial<{
    step_name: string;
    step_class_id: number;
    seq_order: number;
    description: string;
    expected_duration_days: number | null;
    expected_authority: string;
    is_start: boolean;
    is_terminal: boolean;
    is_active: number;
  }>): Promise<LitProceedingStep> {
    const { data } = await saClient.patch(`${BASE}/steps/${stepId}`, payload);
    return data.data;
  },

  async delete(stepId: number): Promise<void> {
    await saClient.delete(`${BASE}/steps/${stepId}`);
  },
};

// ─── Flow edges (super-admin mutations) ──────────────────────────────────────

export const litFlowService = {
  async getByStep(stepId: number): Promise<LitStepFlow[]> {
    const { data } = await saClient.get(`${BASE}/steps/${stepId}/flow`);
    return data.data;
  },

  async add(stepId: number, payload: {
    to_step_id: number;
    condition_label?: string;
    seq_order?: number;
  }): Promise<LitStepFlow> {
    const { data } = await saClient.post(`${BASE}/steps/${stepId}/flow`, payload);
    return data.data;
  },

  async updateLabel(flowId: number, condition_label: string): Promise<LitStepFlow> {
    const { data } = await saClient.patch(`${BASE}/flow/${flowId}`, { condition_label });
    return data.data;
  },

  async delete(flowId: number): Promise<void> {
    await saClient.delete(`${BASE}/flow/${flowId}`);
  },
};

// ─── Graph (dry-run) ─────────────────────────────────────────────────────────

export const litGraphService = {
  async get(proceedingId: number): Promise<LitProceedingGraph> {
    const { data } = await saClient.get(`${BASE}/proceedings/${proceedingId}/graph`);
    return data.data;
  },
};

// ─── Entity-user read helpers (uses regular JWT, not SA token) ───────────────

export const litProceedingsReadService = {
  async getAll(): Promise<LitProceeding[]> {
    const { data } = await apiService.get(`${BASE}/proceedings`);
    return data.data;
  },

  /**
   * Active-only proceeding templates for form dropdowns (Create Litigation picker).
   * Unlike getAll(), this hits /proceedings/dropdown which filters is_active = 1,
   * so inactive templates never leak into the entity-facing picker.
   * `code` carries act_name for the resources that have it.
   */
  async getDropdown(): Promise<{ id: number; name: string; code?: string | null }[]> {
    const { data } = await apiService.get(`${BASE}/proceedings/dropdown`);
    return data.items;
  },

  async getGraph(proceedingId: number): Promise<LitProceedingGraph> {
    const { data } = await apiService.get(`${BASE}/proceedings/${proceedingId}/graph`);
    return data.data;
  },
};
